import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardHeader, EmptyState, ErrorState, Pagination, Skeleton } from '../../../../components/ui';
import { useFetch } from '../../../../hooks/useFetch';
import { labelOf } from '../../../../utils/labels';
import { STOCK_REASONS } from '../../../../constants/status';
import { formatDate } from '../../../../utils/date';
import { money } from '../../../../utils/currency';
import { stockService } from '../../services/stockService';
import type { ReactNode } from 'react';

const refLink = (m) => (m.refType === 'purchase' ? `/purchases/${m.refId}` : m.refType === 'service' ? `/jobs/${m.refId}` : null);

/** Stock in/out history. With itemId: one product; without: all products. */
export function MovementsCard({ itemId, title = 'Stock history', action }: { itemId?: string; title?: string; action?: ReactNode }) {
  const [page, setPage] = useState(1);
  const { data, error, reload } = useFetch(() => stockService.movements({ itemId, page, limit: 20 }), [itemId, page]);
  const rows = data?.data ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader title={title} action={action} className="pb-4" />
      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !data ? (
        <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon="layers" title="No stock movements yet" message="Purchases, jobs and adjustments will show here." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-y border-line text-[13px] text-ink-500">
              <tr>
                <th scope="col" className="px-5 py-2.5 font-normal">Date</th>
                {!itemId && <th scope="col" className="px-5 py-2.5 font-normal">Material</th>}
                <th scope="col" className="px-5 py-2.5 font-normal">Reason</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">In</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Out</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Balance</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Value</th>
              </tr>
            </thead>
            <tbody className="tabular divide-y divide-line">
              {rows.map((m) => {
                const to = refLink(m);
                return (
                  <tr key={m._id}>
                    <td className="whitespace-nowrap px-5 py-2.5">{formatDate(m.date)}</td>
                    {!itemId && (
                      <td className="px-5 py-2.5">
                        {typeof m.itemId === 'object' && <Link to={`/materials/${m.itemId._id}`} className="font-medium text-navy-900 hover:text-brand-600">{m.itemId.name}</Link>}
                      </td>
                    )}
                    <td className="px-5 py-2.5">
                      <Badge tone={m.direction === 'in' ? 'green' : 'neutral'}>{labelOf(STOCK_REASONS, m.reason)}</Badge>
                      {m.note && (to ? <Link to={to} className="ml-2 text-[13px] text-brand-600 hover:underline">{m.note}</Link> : <span className="ml-2 text-[13px] text-ink-500">{m.note}</span>)}
                    </td>
                    <td className="px-5 py-2.5 text-right text-emerald-700">{m.direction === 'in' ? m.qty : ''}</td>
                    <td className="px-5 py-2.5 text-right">{m.direction === 'out' ? m.qty : ''}</td>
                    <td className="px-5 py-2.5 text-right font-medium">{m.balanceAfter ?? '-'}</td>
                    <td className="px-5 py-2.5 text-right text-ink-500">{money(m.value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination meta={data?.meta} onPage={setPage} className="border-t border-line px-5 py-3" />
    </Card>
  );
}

export default MovementsCard;
